import React, { useState, useEffect } from 'react';
import { Sparkles, Key, CheckCircle, RotateCcw } from 'lucide-react';

interface WaitlistTicketData {
  ticketId: string;
  email: string;
  company: string;
  role: string;
  apiKey: string;
  timestamp: string;
}

export const WaitlistSection: React.FC = () => {
  const [firstName, setFirstName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('Lead Software Engineer');
  const [ticket, setTicket] = useState<WaitlistTicketData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('draway_waitlist_ticket');
    if (saved) {
      try {
        setTicket(JSON.parse(saved));
      } catch {
        localStorage.removeItem('draway_waitlist_ticket');
      }
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      alert('Please provide a valid work email address.');
      return;
    }

    setIsSubmitting(true);

    const randomNum = Math.floor(1000 + Math.random() * 9000);
    const ticketId = `DW-BETA-${randomNum}`;
    const previewApiKey = `ak_test_${Math.random().toString(36).substring(2, 10)}${Math.random().toString(36).substring(2, 10)}`;

    const data: WaitlistTicketData = {
      ticketId,
      email,
      company,
      role,
      apiKey: previewApiKey,
      timestamp: new Date().toISOString(),
    };

    try {
      await fetch('/api/contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName,
          company,
          email,
          role,
          ticketId,
          apiKey: previewApiKey,
        }),
      });
    } catch (err) {
      console.warn('Backend sync warning, stored locally:', err);
    } finally {
      localStorage.setItem('draway_waitlist_ticket', JSON.stringify(data));
      setTicket(data);
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    localStorage.removeItem('draway_waitlist_ticket');
    setTicket(null);
    setFirstName('');
    setCompany('');
    setEmail('');
  };

  return (
    <section className="section-dark" style={{ paddingTop: 0 }} id="waitlist">
      <div className="container">
        <div className="waitlist-card">
          <div style={{ maxWidth: 680, margin: '0 auto', textAlign: 'center' }}>
            <span className="eyebrow" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
              <Sparkles size={14} /> VIP Early Access Registration
            </span>
            <h3 className="heading-xl" style={{ marginTop: 8 }}>
              Reserve your early-access credentials
            </h3>
            <p className="body-md" style={{ marginTop: 8 }}>
              We are onboarding engineering teams in cohorts. Register your work email to receive early sandbox API keys and lock in 50% lifetime platform pricing.
            </p>

            {!ticket ? (
              <form onSubmit={handleSubmit} style={{ marginTop: 32, textAlign: 'left' }}>
                <div className="waitlist-form-grid">
                  <div>
                    <label className="form-label" htmlFor="waitlist-first-name">First Name</label>
                    <input
                      className="text-input"
                      id="waitlist-first-name"
                      type="text"
                      placeholder="Alex"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="form-label" htmlFor="waitlist-company">Company / Organization</label>
                    <input
                      className="text-input"
                      id="waitlist-company"
                      type="text"
                      placeholder="Acme Systems"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group-full">
                    <label className="form-label" htmlFor="waitlist-email">Work Email</label>
                    <input
                      className="text-input"
                      id="waitlist-email"
                      type="email"
                      placeholder="alex@acme.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group-full">
                    <label className="form-label" htmlFor="waitlist-role">Engineering Role</label>
                    <select
                      className="text-input"
                      id="waitlist-role"
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                    >
                      <option value="CTO / VP Engineering">CTO / VP Engineering</option>
                      <option value="Lead Software Engineer">Lead Software Engineer</option>
                      <option value="Security Architect">Security Architect</option>
                      <option value="Product Founder">Product Founder</option>
                      <option value="DevOps / Platform Lead">DevOps / Platform Lead</option>
                    </select>
                  </div>
                </div>

                <div style={{ marginTop: 24, textAlign: 'center' }}>
                  <button 
                    type="submit" 
                    className="button-primary" 
                    disabled={isSubmitting} 
                    style={{ padding: '14px 32px', fontSize: 15, opacity: isSubmitting ? 0.7 : 1, cursor: isSubmitting ? 'wait' : 'pointer' }}
                  >
                    {isSubmitting ? 'Saving to database...' : 'Reserve VIP beta ticket →'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="waitlist-ticket active" style={{ marginTop: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #374151', paddingBottom: 16 }}>
                  <div style={{ textAlign: 'left' }}>
                    <span className="eyebrow">Draway Private Access Ticket</span>
                    <div className="heading-lg" style={{ color: '#FFFFFF', marginTop: 4 }}>
                      {ticket.ticketId}
                    </div>
                  </div>
                  <div className="status-badge">
                    <CheckCircle size={14} /> Confirmed
                  </div>
                </div>

                <div style={{ marginTop: 20, textAlign: 'left', fontFamily: 'var(--font-code)', fontSize: 13, lineHeight: 1.8 }}>
                  <div>Assigned To: <span style={{ color: '#FFFFFF' }}>{ticket.email}</span> ({ticket.company || 'Enterprise Org'})</div>
                  <div>Developer Role: <span style={{ color: '#D7D7D7' }}>{ticket.role}</span></div>
                  <div>
                    Sandbox Key: <span style={{ color: '#6D7CFF' }}><Key size={12} style={{ display: 'inline', marginRight: 4 }} />{ticket.apiKey}</span>
                  </div>
                  <div style={{ color: '#10B981', marginTop: 8 }}>
                    ✓ Access Slot Reserved • Wave 1 Alpha Deployment
                  </div>
                </div>

                <div style={{ marginTop: 24 }}>
                  <button
                    type="button"
                    className="button-secondary"
                    onClick={handleReset}
                    style={{ fontSize: 13 }}
                  >
                    <RotateCcw size={14} style={{ marginRight: 6 }} />
                    Register another work email
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
